// 66a — WHAT A MENTION IS. One definition, read by BOTH planes.
//
// TWO PLANES ASKED THIS QUESTION AND ANSWERED IT DIFFERENTLY. The client's
// dashboard counts unread mentions of me (src/lib/mentionFilter.ts); the
// server warns at send time that a mentioned colleague is off the recipient
// set (server/src/routes/chatMessages.js, Phase 30 Stage-3 R2). Until brief 67
// §1 each carried its own matcher:
//
//   client  new RegExp(`@${token}(?![\p{L}\p{N}_])`, "iu")   — Unicode, end only
//   server  text.match(/@(\w+)/g)                            — ASCII,   no bound
//
// So `@allé` tokenised as `all` on the server and as nothing on the client:
// one plane notified the whole firm, the other said no mention had happened.
// `@ÉA` was a mention the server could not see at all, so it could never warn
// that ÉA was off-set. Measured, not reasoned — 11 of 46 recorded cases were
// red on the server plane and 8 of 49 on the client's.
//
// TWO HAND-MAINTAINED COPIES OF ONE RULE IS THE DEFECT, not the two spellings
// of it, so this is not "both edited correctly". Both planes now call this
// module and neither owns a matcher. The shape is the one this tree already
// established four times over for exactly this reason — a pure `.mjs` under
// src/lib, imported by the client and require()d by the server (Node 20.19+
// resolves require(esm) synchronously; this deploy is 20.20):
// caseRefPattern.mjs, coiMatch.mjs, cddIdentity.mjs, clientMatters.mjs.
// cdd.js:37-38 calls it "the F1/F2 anti-drift collapse: one function, two call
// sites, byte-identical".
//
// RULED 2026-08-11 by Laurent, brief 66 §3 and brief 67 §1:
//
//   WORD-BOUNDED AND CASE-INSENSITIVE, AND THE SAME RULE APPLIES TO BOTH
//   HALVES (@all and @initials alike). A mention matches when the token ends
//   at a space, punctuation or end of message. `@ALL` and `@all` both work.
//
//   THE BOUNDARY IS RULED AT BOTH ENDS. A mention counts only when the `@` is
//   itself preceded by a space, punctuation or the start of the message, so
//   `x@all.be` pasted into chat stops notifying the firm.
//
//   THE TOKEN CLASS IS UNICODE ON BOTH PLANES. The server adopts it; `@allé`
//   must not tokenise as `all` on one plane and as nothing on the other.
//
// WHERE THE BOUNDARY LANDS IS THE WHOLE BEHAVIOUR, so it is characterised
// rather than described: recordings/66-mention-bounds replays every case
// through BOTH planes, with a --prove-red that undoes each half of the rule in
// memory and requires the cases naming it to redden on both.
//
// NO LOOKBEHIND, DELIBERATELY. `(?<![\p{L}\p{N}_])@` is the obvious spelling
// of the leading boundary and it is not available here: vite's default target
// includes safari14, lookbehind landed in Safari 16.4, and esbuild lowers an
// unsupported regex literal to a `new RegExp` that throws at first use rather
// than at parse — so the failure would be a dead dashboard, not a red build.
// There is no lookbehind anywhere in src/ today. The boundary is therefore a
// test on the character before the `@`, which reads like the ruling anyway.
//
// PURE: no prisma, no I/O, no DOM. It owns neither side of the wire.

// THE TOKEN CLASS, WRITTEN ONCE — this is the whole anti-drift claim in one
// constant. It builds both the token matcher and the boundary test, so the two
// ends of a token cannot disagree about what a word character is, and neither
// can the two planes.
const WORD_CLASS = '[\\p{L}\\p{N}_]';
const WORD = new RegExp(`^${WORD_CLASS}$`, 'u');
const AT_TOKEN = new RegExp(`@${WORD_CLASS}+`, 'gu');

// Case folding, in one place, so "case-insensitive" is one decision. Both
// planes already upper-case the thing they compare against — server/src/lib/
// initials.js:14,17 and src/utils.ts:572-576 — so this is the same fold.
const fold = (s) => s.toUpperCase();

/** The code point ending at `i`, as a string; "" at the start of the text.
 *  Two units when that code point is astral: `text[i - 1]` alone is a lone low
 *  surrogate, which is not `\p{L}`, and would call a letter a boundary. */
function charBefore(text, i) {
  if (i <= 0) return '';
  const c = text.charCodeAt(i - 1);
  if (c >= 0xdc00 && c <= 0xdfff && i >= 2) return text.slice(i - 2, i);
  return text.charAt(i - 1);
}

/** Every token `text` mentions, upper-cased. `@` runs that fail the leading
 *  boundary are not tokens at all, which is why an email address contributes
 *  nothing rather than contributing its domain. */
export function mentionTokens(text) {
  const out = new Set();
  const src = typeof text === 'string' ? text : '';
  if (!src.includes('@')) return out;
  for (const m of src.matchAll(AT_TOKEN)) {
    const before = charBefore(src, m.index);
    if (before && WORD.test(before)) continue;
    out.add(fold(m[0].slice(1)));
  }
  return out;
}

/** Does `text` name `token` as a mention? Exact token, case-insensitive.
 *  The token never reaches a regex constructor — it is compared as a string —
 *  so collaborator-supplied initials carry no pattern meaning at all. */
export function namesToken(text, token) {
  const want = typeof token === 'string' ? token : '';
  if (!want) return false;
  const folded = fold(want);
  return mentionTokens(text).has(folded);
}
